import React, { createContext, useContext, useReducer, ReactNode, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { addToWishlist as addToWishlistService, getUserWishlist, removeFromWishlist as removeFromWishlistService } from '../services/AdditionalFirestoreService';

interface WishlistItem {
  id: string;
  userId: string;
  productId: string;
  addedAt: Date;
}

interface WishlistState {
  items: WishlistItem[];
}

type WishlistAction =
  | { type: 'SET_WISHLIST'; payload: WishlistItem[] }
  | { type: 'ADD_ITEM'; payload: WishlistItem }
  | { type: 'REMOVE_ITEM'; payload: string }; // payload is wishlist item id

const WishlistContext = createContext<{
  state: WishlistState;
  dispatch: React.Dispatch<WishlistAction>;
  addToWishlist: (productId: string) => Promise<void>;
  removeFromWishlist: (wishlistId: string) => Promise<void>;
  isInWishlist: (productId: string) => boolean;
} | undefined>(undefined);

const wishlistReducer = (state: WishlistState, action: WishlistAction): WishlistState => {
  switch (action.type) {
    case 'SET_WISHLIST':
      return {
        ...state,
        items: action.payload
      };
    case 'ADD_ITEM':
      // Check if item already exists
      const exists = state.items.some(item => item.productId === action.payload.productId);
      if (!exists) {
        return {
          ...state,
          items: [...state.items, action.payload]
        };
      }
      return state;
    case 'REMOVE_ITEM':
      return {
        ...state,
        items: state.items.filter(item => item.id !== action.payload)
      };
    default:
      return state;
  }
};

export const WishlistProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(wishlistReducer, { items: [] });
  const { currentUser } = useAuth();

  useEffect(() => {
    const loadWishlist = async () => {
      if (currentUser) {
        try {
          const wishlist = await getUserWishlist(currentUser.uid);
          dispatch({ type: 'SET_WISHLIST', payload: wishlist });
        } catch (error) {
          console.error('Error loading wishlist:', error);
        }
      } else {
        dispatch({ type: 'SET_WISHLIST', payload: [] });
      }
    };

    loadWishlist();
  }, [currentUser]);

  const addToWishlist = async (productId: string) => {
    if (!currentUser) {
      throw new Error('User must be logged in to add to wishlist');
    }

    try {
      const wishlistId = await addToWishlistService(currentUser.uid, productId);
      const newWishlistItem: WishlistItem = {
        id: wishlistId,
        userId: currentUser.uid,
        productId,
        addedAt: new Date()
      };
      dispatch({ type: 'ADD_ITEM', payload: newWishlistItem });
    } catch (error) {
      console.error('Error adding to wishlist:', error);
      throw error;
    }
  };

  const removeFromWishlist = async (wishlistId: string) => {
    if (!currentUser) {
      throw new Error('User must be logged in to remove from wishlist');
    }

    try {
      await removeFromWishlistService(currentUser.uid, wishlistId);
      dispatch({ type: 'REMOVE_ITEM', payload: wishlistId });
    } catch (error) {
      console.error('Error removing from wishlist:', error);
      throw error;
    }
  };

  const isInWishlist = (productId: string) => {
    return state.items.some(item => item.productId === productId);
  };

  return (
    <WishlistContext.Provider value={{ 
      state, 
      dispatch, 
      addToWishlist, 
      removeFromWishlist, 
      isInWishlist 
    }}>
      {children}
    </WishlistContext.Provider>
  );
};

export const useWishlist = () => {
  const context = useContext(WishlistContext);
  if (context === undefined) {
    throw new Error('useWishlist must be used within a WishlistProvider');
  }
  return context;
};

export const useWishlistCount = () => {
  const { state } = useWishlist();
  return state.items.length;
};